/**
 * Store-first token validation utilities
 *
 * These functions prefer reading from Zustand store for runtime consistency.
 * Cookie is only used as fallback when store is empty (e.g., page just loaded).
 *
 * This file exists to avoid circular dependency between auth.ts and team.ts.
 */

import { useTeamStore } from '@/stores/team'
import { getTeamTokenData, TEAM_TOKEN_REFRESH_THRESHOLD_MS } from '@/utils/auth'

/**
 * Check if team token is expired, preferring Store over Cookie
 */
export function isTeamTokenExpiredFromStore(): boolean {
  const teamTokenData = useTeamStore.getState().teamTokenData

  if (!teamTokenData || !teamTokenData.expiryTime) {
    // Fallback to cookie
    const cookieData = getTeamTokenData()
    if (!cookieData || !cookieData.expiryTime) return true
    return Date.now() >= cookieData.expiryTime
  }

  return Date.now() >= teamTokenData.expiryTime
}

/**
 * Check if team token is expiring soon, preferring Store over Cookie
 */
export function isTeamTokenExpiringSoonFromStore(): boolean {
  const teamTokenData = useTeamStore.getState().teamTokenData

  if (!teamTokenData || !teamTokenData.expiryTime) {
    // Fallback to cookie
    const cookieData = getTeamTokenData()
    if (!cookieData || !cookieData.expiryTime) return true
    const remainingTime = cookieData.expiryTime - Date.now()
    return remainingTime <= TEAM_TOKEN_REFRESH_THRESHOLD_MS
  }

  const remainingTime = teamTokenData.expiryTime - Date.now()
  return remainingTime <= TEAM_TOKEN_REFRESH_THRESHOLD_MS
}

/**
 * Get team ID and resID, preferring Store over Cookie
 */
export function getTeamIdFromStore(): { teamId: number; teamResID: string } | null {
  const teamTokenData = useTeamStore.getState().teamTokenData

  if (teamTokenData?.teamId && teamTokenData?.teamResID) {
    return { teamId: teamTokenData.teamId, teamResID: teamTokenData.teamResID }
  }

  // Fallback to cookie if store is empty (e.g., page just loaded)
  const cookieData = getTeamTokenData()
  if (cookieData?.teamId && cookieData?.teamResID) {
    return { teamId: cookieData.teamId, teamResID: cookieData.teamResID }
  }

  return null
}
